import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Mail, Lock, Loader2, RefreshCw } from "lucide-react";
import { ForteShield } from "../components/BrandLogo";

async function hardResetClientCache() {
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch (_) { /* ignore */ }
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch (_) { /* ignore */ }
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch (_) { /* ignore */ }
  // Forçar reload sem cache + cache-busting na URL.
  const url = new URL(window.location.href);
  url.searchParams.set("_cb", Date.now().toString());
  window.location.replace(url.toString());
}

export default function LoginPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);

  if (user) return <Navigate to="/painel" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      nav(u.role === "indicador" ? "/painel" : "/painel");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Falha no login. Verifique suas credenciais.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex auth-bg relative overflow-hidden">
      {/* Left visual */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1639674242803-a9de33b3a835?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGJsYWNrJTIwYW5kJTIwZ29sZHxlbnwwfHx8fDE3ODA1MTkxODJ8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/40 to-black/90" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-4">
            <ForteShield size={56} />
            <div>
              <div className="font-display font-bold text-2xl tracking-[0.04em]">FORTE CAPITAL</div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4AF37] mt-1">Análise Privada</div>
            </div>
          </div>
          <div>
            <h2 className="font-display text-4xl xl:text-5xl font-bold leading-tight">
              Inteligência financeira <br/>
              <span className="text-[#D4AF37]">com discrição.</span>
            </h2>
            <p className="text-[#A3A3A3] mt-4 max-w-md text-[15px] leading-relaxed">
              Painel interno restrito para administradores, gestoras e indicadores credenciados.
              Acesso exclusivo, dados protegidos.
            </p>
            <div className="gold-line w-32 mt-6" />
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 relative">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="lg:hidden flex flex-col items-center gap-3 mb-10">
            <ForteShield size={56} />
            <span className="font-display font-bold text-base tracking-[0.04em]">FORTE CAPITAL</span>
          </div>

          <div className="mb-8">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Acesso Interno</div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2 tracking-tight">Entrar no painel</h1>
            <p className="text-[#A3A3A3] mt-2 text-sm">Utilize suas credenciais corporativas.</p>
          </div>

          <div className="space-y-5">
            <div>
              <label className="fc-label">E-mail</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]" />
                <input
                  type="email"
                  className="fc-input pl-10"
                  placeholder="seu@email.com.br"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  required
                />
              </div>
            </div>
            <div>
              <label className="fc-label">Senha</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]" />
                <input
                  type="password"
                  className="fc-input pl-10"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  required
                />
              </div>
            </div>

            {error && (
              <div className="text-[13px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="fc-btn-gold w-full flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="login-submit-button"
            >
              {loading ? <><Loader2 size={16} className="animate-spin"/> Entrando...</> : "Entrar"}
            </button>

            <button
              type="button"
              onClick={async () => { setClearing(true); await hardResetClientCache(); }}
              disabled={clearing}
              className="w-full flex items-center justify-center gap-1.5 text-[11.5px] text-[#737373] hover:text-amber-300 transition-colors py-1.5 disabled:opacity-60"
              data-testid="login-clear-cache"
              title="Útil se o navegador estiver com cache antigo (PC mostra erro de credenciais mas celular funciona)"
            >
              {clearing ? <Loader2 size={11} className="animate-spin"/> : <RefreshCw size={11}/>}
              Problemas para entrar? Limpar cache do navegador
            </button>
          </div>

          <div className="mt-10 pt-6 border-t border-[#1f1f1f] text-xs text-[#737373] text-center">
            © {new Date().getFullYear()} Forte Capital — Acesso restrito e monitorado.
          </div>
        </form>
      </div>
    </div>
  );
}
